import type { ISODate } from "@inrev/common";
import { DateTime } from "luxon";
import type { FinancialPeriodDto } from "../../utils";
import type { AnalysisSourceLookup } from "./sources";
import { WIP } from "./wipRowIds";

// A WIP statement is dated independently of the financial period, so it is matched by date. This
// mirrors the backend rule (WorkInProgressStatement.isValid + ContractSuretyAccountUnderwriting
// .gracePeriodMonths): a WIP is valid for a period when it is dated on or after the period end date
// and no more than one year plus this grace period after it.
const WIP_GRACE_PERIOD_MONTHS = 3;

/** The WIP fields the analysis metrics read, structurally satisfied by both the admin and agent WIP DTOs. */
export type WipStatementForAnalysis = {
	date: ISODate;
	projectBacklogCost?: number | null;
	projectBacklogGrossProfit?: number | null;
};

const isValidForPeriod = (wipDate: DateTime, periodEndDate: DateTime): boolean => {
	const invalidAfter = periodEndDate.plus({ years: 1, months: WIP_GRACE_PERIOD_MONTHS });
	return wipDate >= periodEndDate && wipDate <= invalidAfter;
};

const buildLookup = (wip: WipStatementForAnalysis): Map<string, number> => {
	const byRowId = new Map<string, number>();
	if (typeof wip.projectBacklogCost === "number" && Number.isFinite(wip.projectBacklogCost))
		byRowId.set(WIP.projectBacklogCost, wip.projectBacklogCost);
	if (
		typeof wip.projectBacklogGrossProfit === "number" &&
		Number.isFinite(wip.projectBacklogGrossProfit)
	)
		byRowId.set(WIP.projectBacklogGrossProfit, wip.projectBacklogGrossProfit);
	return byRowId;
};

/**
 * Binds account WIP statements to the analysis `wip` source. For each period we take the latest WIP
 * statement that is valid as of the period end date (see {@link isValidForPeriod}); periods with no
 * valid WIP resolve the source to null, so backlog metrics render "--".
 */
export const wipSource = (wipStatements: WipStatementForAnalysis[]): AnalysisSourceLookup => ({
	key: "wip",
	lookupForPeriod: (period: FinancialPeriodDto): Map<string, number> | null => {
		const periodEndDate = DateTime.fromISO(period.periodEndDate);
		let latestValid: { wip: WipStatementForAnalysis; date: DateTime } | null = null;
		for (const wip of wipStatements) {
			const wipDate = DateTime.fromISO(wip.date);
			if (!isValidForPeriod(wipDate, periodEndDate)) continue;
			if (latestValid === null || wipDate > latestValid.date) latestValid = { wip, date: wipDate };
		}
		return latestValid === null ? null : buildLookup(latestValid.wip);
	},
});
